import { formatRelativeTime } from '@customerio/pluma-components/react/utils/formatted-relative-time';

type TrialExpiryDescriptionProps = {
  trialExpired: boolean;
  /** Whether the account has a payment method but the trial hasn't finished. */
  isPendingCheckout: boolean;
  daysLeftInTrial: number;
  /** When the trial expires, unix seconds, or null. */
  trialExpiresAt: number | null;
  canViewBilling: boolean;
};

/**
 * The trial-status copy shown as the plan-card trial label. The phrasing branches on whether the
 * trial has expired, whether checkout is pending, and how many days remain. The relative distance
 * (e.g. "in 3 days") is computed with Pluma's `formatRelativeTime`.
 */
// Faithful port of the trial-status copy; the branching is the copy itself.
// fallow-ignore-next-line complexity
export function TrialExpiryDescription({
  trialExpired,
  isPendingCheckout,
  daysLeftInTrial,
  trialExpiresAt,
  canViewBilling,
}: TrialExpiryDescriptionProps) {
  if (trialExpired) {
    if (isPendingCheckout) {
      return null;
    }
    if (canViewBilling) {
      return <>Your trial has ended</>;
    }
    return <>You cannot access your data until the account is upgraded.</>;
  }

  if (daysLeftInTrial < 1) {
    let base: string;
    if (daysLeftInTrial === 0) {
      base = 'Your trial ends today';
    } else {
      base = 'Your trial has ended';
    }
    if (isPendingCheckout) {
      return <>{base} and will automatically upgrade</>;
    }
    return <>{base}</>;
  }

  if (trialExpiresAt == null) {
    if (isPendingCheckout) {
      return <>Your trial will automatically upgrade soon</>;
    }
    return <>Your trial will end soon</>;
  }

  // `distance` already carries its own "in "/" ago" direction, so the sentences below don't add
  // their own. `absoluteFormatDiffCutoff: null` keeps this relative even past Pluma's default 30-day
  // cutoff (matching the old date-fns behavior, which never fell back to an absolute date).
  const distance = formatRelativeTime(trialExpiresAt, new Date(), {
    absoluteFormatDiffCutoff: null,
    shouldUppercaseFirst: false,
  });
  if (isPendingCheckout) {
    return <>Your trial will automatically upgrade {distance}</>;
  }
  return <>Your trial ends {distance}</>;
}
